import { FC, ReactElement } from "react";
import { Skeleton, Typography, Box, Chip } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineDot,
  timelineItemClasses,
} from "@mui/lab";
import { formatDistanceToNow } from "date-fns";

import { IconImage } from "@/components";
import { useGetAllTradeDataLogsQuery } from "@/services";
import { list_img } from "@/constants";
import { getTaskHealth } from "@/utils";

import {
  HighlightedText,
  StyledBody2,
  StyledCaption,
  StyledHeader,
  StyledListItemText,
  StyledSkeletonBox,
  StyledTimelineContent,
  StyledTimelineItem,
  StyledTimelineSeparator,
} from "./style";

const TimelineData: FC = (): ReactElement => {
  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTradeDataLogsQuery(
    {
      input: {
        clientId: "",
        uploadTime: "",
      },
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const renderSkeleton = () => (
    <StyledSkeletonBox>
      <StyledTimelineItem>
        <StyledTimelineSeparator>
          <TimelineConnector />
          <TimelineDot>
            <Skeleton variant="circular" />
          </TimelineDot>
          <TimelineConnector />
        </StyledTimelineSeparator>
        <StyledListItemText
          primary={
            <>
              <Skeleton variant="text" width={80} />
              <Skeleton variant="text" width="60%" />
            </>
          }
        />
      </StyledTimelineItem>
    </StyledSkeletonBox>
  );

  return (
    <>
      <StyledHeader>
        <Typography variant="h5">Logs</Typography>
      </StyledHeader>
      <Timeline
        sx={{
          width: "433px",
          padding: "0 8px 8px 8px",
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
      >
        {isLoading || isFetching ? (
          Array.from(new Array(5)).map(() => renderSkeleton())
        ) : (
          <StyledTimelineItem>
            <StyledTimelineContent>
              {apiData && (
                <StyledTimelineItem key={apiData.status}>
                  <StyledTimelineSeparator>
                    <TimelineConnector />
                    <TimelineDot>
                      <IconImage src={list_img} size="auto" />
                    </TimelineDot>
                    <TimelineConnector />
                  </StyledTimelineSeparator>
                  <StyledListItemText
                    primary={
                      <>
                        <StyledCaption variant="caption">
                          {apiData.uploadTime}
                        </StyledCaption>
                        <StyledBody2 variant="body2">
                          Status: <HighlightedText>{apiData.status}</HighlightedText>
                        </StyledBody2>
                        <StyledBody2 variant="body2">
                          Progress: <HighlightedText>{apiData.progress}%</HighlightedText>
                        </StyledBody2>
                        
                        {/* Heartbeat Information */}
                        {apiData.lastHeartbeat && (
                          <Box sx={{ marginTop: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              Last heartbeat: {formatDistanceToNow(new Date(apiData.lastHeartbeat))} ago
                            </Typography>
                            <Box sx={{ marginTop: 0.5 }}>
                              <Chip
                                label={getTaskHealth(apiData).health}
                                size="small"
                                color={
                                  getTaskHealth(apiData).health === 'healthy' ? 'success' :
                                  getTaskHealth(apiData).health === 'stale' ? 'warning' : 'error'
                                }
                                variant="outlined"
                              />
                            </Box>
                            {getTaskHealth(apiData).message && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginTop: 0.5 }}>
                                {getTaskHealth(apiData).message}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </>
                    }
                  />
                </StyledTimelineItem>
              )}
            </StyledTimelineContent>
          </StyledTimelineItem>
        )}
      </Timeline>
    </>
  );
};

export default TimelineData;
